// Simple API client for school operations
// The actual business logic is in /backend_auth/services/schoolService.js
import { API_BASE_URL } from '../config';

const AUTH_API_BASE_URL = API_BASE_URL;

export const schoolsAPI = {
  /**
   * Create a new school
   */
  async createSchool(schoolData, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${AUTH_API_BASE_URL}/api/schools`, {
      method: 'POST',
      headers,
      body: JSON.stringify(schoolData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to create school';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Get all schools (automatically filtered by user's organization from JWT)
   */
  async getSchools(authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const url = `${AUTH_API_BASE_URL}/api/schools`;
    const response = await fetch(url, { headers });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to fetch schools';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Get all schools for an organization (alias for backward compatibility)
   */
  async getSchoolsByOrganization(organization_id, authToken = null) {
    // Now just calls getSchools since organization is extracted from JWT
    return this.getSchools(authToken);
  },

  /**
   * Get school by ID
   */
  async getSchoolById(schoolId, authToken = null) {
    const headers = {};
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${AUTH_API_BASE_URL}/api/schools/${schoolId}`, { headers });
    
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to fetch school';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Update school information
   */
  async updateSchool(schoolId, updateData, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${AUTH_API_BASE_URL}/api/schools/${schoolId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to update school';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Delete school (hard delete)
   */
  async deleteSchool(schoolId, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${AUTH_API_BASE_URL}/api/schools/${schoolId}`, {
      method: 'DELETE',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to delete school';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Update school timezone
   */
  async updateSchoolTimezone(schoolId, timezone, authToken = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${AUTH_API_BASE_URL}/api/schools/${schoolId}/timezone`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ timezone }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to update school timezone';
      throw new Error(errorMessage);
    }

    return await response.json();
  },

  /**
   * Get supported timezones
   */
  async getSupportedTimezones(authToken = null) {
    const headers = {};
    
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }

    const response = await fetch(`${AUTH_API_BASE_URL}/api/schools/timezones`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.details || errorData.error || 'Failed to fetch supported timezones';
      throw new Error(errorMessage);
    }

    return await response.json();
  }
};

// Export as default for backward compatibility
export default schoolsAPI;
